import { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Heart, Award, Truck, Shield, Users, Target, Sparkles, Star, Gift, Scissors, Smile } from 'lucide-react';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:5000'
  : 'https://kiyafet-magazasi-backend.vercel.app';

const iconMap: Record<string, any> = { Heart, Award, Truck, Shield, Users, Target, Sparkles, Star, Gift, Scissors, Smile };

export function About() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/hakkimizda`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Fallback defaults
  const hero = data?.hero || { baslik: 'Hakkımızda', altBaslik: 'Kadınların stil ve zarafetini yansıtan, kaliteli ve özgün tasarımlar sunuyoruz.' };
  const hikaye = data?.hikaye || {
    baslik: 'Bizim Hikayemiz',
    paragraflar: [
      '<strong class="text-pink-600">Ladiora Boutique</strong>, kadınların gardıroplarına değer katacak, onları özel hissettirecek tasarımlar yaratma hayaliyle kuruldu.',
      'Modern kadının dinamik yaşam tarzını anlayan ve bunu tasarımlarına yansıtan bir marka olarak, her kadının kendini ifade edebileceği, özgün ve kaliteli parçalar sunuyoruz.',
      "Casual'dan şık gece kıyafetlerine, Hoodie Cape'lerden Kimono'lara kadar geniş ürün yelpazemizle, her tarza ve her anınıza uygun seçenekler sunmayı hedefliyoruz.",
      '<strong>Kalite, özgünlük ve müşteri memnuniyeti</strong> bizim için vazgeçilmezdir. Her ürünümüz özenle seçilir, kalitesi test edilir ve sevgiyle size ulaştırılır.'
    ],
    resim: 'https://images.unsplash.com/photo-1760287363707-851f4780b98c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxmYXNoaW9uJTIwYm91dGlxdWUlMjBzdG9yZSUyMGludGVyaW9yfGVufDF8fHx8MTc3NDU5ODQzNnww&ixlib=rb-4.1.0&q=80&w=1080',
    overlayBaslik: 'Yeni & Fresh',
    overlayAlt: 'Taze Koleksiyonlar'
  };
  const features = data?.features || [
    { icon: 'Star', title: 'Özenle Seçilmiş Koleksiyonlar', description: 'Her ürün, kalite ve tasarım açısından titizlikle seçilir. Gardırobunuza değer katacak parçalar sunuyoruz.' },
    { icon: 'Gift', title: 'Kişiye Özel Deneyim', description: 'Her müşterimiz bizim için özeldir. Size en uygun stil ve bedeni bulmanız için buradayız.' },
    { icon: 'Scissors', title: 'Benzersiz Tasarımlar', description: 'Sıradanlıktan uzak, kendinizi ifade edebileceğiniz özgün ve şık tasarımlar.' },
    { icon: 'Smile', title: 'Mutlu Alışveriş', description: 'Kolay iade, hızlı kargo ve sürekli destek ile alışveriş deneyiminizi keyifli hale getiriyoruz.' },
  ];
  const featuresBaslik = data?.features_baslik || { baslik: 'Sizin İçin', altBaslik: 'Ladiora Boutique olarak size özel olarak sunduğumuz değerler:' };

  const process_steps = data?.process || [
    { step: '01', title: 'Tasarım & Seçim', description: 'Her sezon moda trendlerini analiz eder, kaliteli ve özgün parçaları sizin için özenle seçeriz.' },
    { step: '02', title: 'Kalite Kontrolü', description: 'Tüm ürünlerimiz depoya gelmeden önce detaylı kalite kontrolünden geçer.' },
    { step: '03', title: 'Özenli Paketleme', description: 'Siparişiniz özel paketleme ile hazırlanır ve size güvenle ulaştırılır.' },
    { step: '04', title: 'Hızlı Teslimat', description: 'Kargo sürecinizi adım adım takip edebilir, ürününüze hızlıca kavuşabilirsiniz.' },
  ];
  const processBaslik = data?.process_baslik || { baslik: 'Nasıl Çalışıyoruz?', altBaslik: 'Ürünleriniz sizlere ulaşana kadar geçen süreçteki adımlarımız:' };

  const degerler = data?.degerler || {
    baslik: 'Temel Değerlerimiz',
    altBaslik: 'Ladiora Boutique olarak, işimizin temelini oluşturan prensiplerimiz:',
    items: [
      { icon: 'Heart', title: 'Müşteri Memnuniyeti', description: 'Müşterilerimizin memnuniyeti bizim için her şeyden önce gelir. Kaliteli ürün ve hizmet sunmak misyonumuzdur.' },
      { icon: 'Award', title: 'Kalite & Güven', description: 'Tüm ürünlerimiz en yüksek kalite standartlarında üretilir ve güvenilir tedarikçilerle çalışırız.' },
      { icon: 'Sparkles', title: 'Yenilikçilik', description: 'Moda trendlerini yakından takip eder, koleksiyonlarımızı sürekli yeniler ve geliştiririz.' },
      { icon: 'Shield', title: 'Güvenli Alışveriş', description: 'Online alışveriş deneyiminizin güvenli ve sorunsuz olması için gerekli tüm önlemleri alırız.' },
    ]
  };
  const misyonVizyon = data?.misyon_vizyon || { misyon: 'Modern kadının ihtiyaçlarını anlayan, kaliteli ve uygun fiyatlı moda ürünlerini Türkiye\'nin her yerine ulaştırmak. Müşterilerimize güvenilir, keyifli ve sorunsuz bir alışveriş deneyimi sunmak.', vizyon: 'Türkiye\'nin en sevilen ve güvenilen online moda platformlarından biri olmak. Her kadının stilini bulabileceği, kendini özel hissedebileceği bir marka olmak.' };
  const nedenBiz = data?.neden_biz || [
    { icon: 'Award', title: 'Premium Kalite', description: 'En kaliteli kumaşlar ve işçilik garantisi ile üretilen ürünler.' },
    { icon: 'Truck', title: 'Hızlı Teslimat', description: '1.500 TL üzeri ücretsiz kargo ve 3-5 iş günü içinde teslimat.' },
    { icon: 'Shield', title: 'Güvenli Alışveriş', description: 'WhatsApp sipariş ve havale/EFT ile güvenli ödeme.' },
  ];
  const cta = data?.cta || { baslik: 'Ladiora Ailesine Katılın', altBaslik: 'Yeni koleksiyonlarımızdan haberdar olmak ve özel fırsatları kaçırmamak için bizi takip edin!' };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-br from-pink-600 to-purple-600 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">{hero.baslik}</h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto opacity-95">{hero.altBaslik}</p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Yükleniyor...</p>
          </div>
        ) : (
        <div className="max-w-7xl mx-auto px-4 py-16">
          {/* Story Section */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">{hikaye.baslik}</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                {hikaye.paragraflar.map((p: string, i: number) => (
                  <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
                ))}
              </div>
            </div>
            <div className="relative">
              {hikaye.resim && (
                <img src={hikaye.resim} alt="Ladiora Boutique" className="rounded-2xl shadow-2xl" />
              )}
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-pink-600 to-purple-600 text-white p-6 rounded-xl shadow-xl">
                <p className="text-2xl font-bold mb-1">{hikaye.overlayBaslik || 'Yeni & Fresh'}</p>
                <p className="text-sm opacity-90">{hikaye.overlayAlt || 'Taze Koleksiyonlar'}</p>
              </div>
            </div>
          </div>

          {/* Sizin İçin - What We Offer */}
          <div className="mb-20 bg-gradient-to-br from-pink-50 to-purple-50 rounded-3xl p-8 md:p-12">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-4">{featuresBaslik.baslik}</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">{featuresBaslik.altBaslik}</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature: any, index: number) => {
                const IconComp = iconMap[feature.icon] || Star;
                return (
                  <div key={index} className="bg-white rounded-xl p-6 hover:shadow-xl transition-shadow">
                    <div className="size-14 bg-gradient-to-br from-pink-600 to-purple-600 rounded-full flex items-center justify-center mb-4">
                      <IconComp className="size-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                    <p className="text-gray-700 leading-relaxed">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Nasıl Çalışıyoruz - Process */}
          <div className="mb-20">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-4">{processBaslik.baslik}</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">{processBaslik.altBaslik}</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {process_steps.map((item: any, index: number) => (
                <div key={index} className="relative group">
                  <div className="bg-white border-2 border-pink-200 rounded-2xl p-6 hover:shadow-xl hover:border-pink-400 transition-all">
                    <div className="size-16 bg-gradient-to-br from-pink-600 to-purple-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                      <span className="text-2xl font-bold text-white">{item.step}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-center text-sm">{item.description}</p>
                  </div>
                  {index < process_steps.length - 1 && (
                    <div className="hidden lg:block absolute top-8 -right-3 w-6 h-0.5 bg-gradient-to-r from-pink-300 to-purple-300"></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Values - Core Principles */}
          <div className="mb-20 bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-8 md:p-12">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-4">{degerler.baslik}</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">{degerler.altBaslik}</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {degerler.items.map((value: any, index: number) => {
                const IconComp = iconMap[value.icon] || Heart;
                return (
                  <div key={index} className="bg-white rounded-xl p-6 hover:shadow-xl transition-shadow">
                    <div className="size-14 bg-gradient-to-br from-pink-600 to-purple-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                      <IconComp className="size-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">{value.title}</h3>
                    <p className="text-gray-700 leading-relaxed text-center text-sm">{value.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mission & Vision */}
          <div className="grid md:grid-cols-2 gap-8 mb-20">
            <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="size-12 bg-pink-600 rounded-full flex items-center justify-center">
                  <Target className="size-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Misyonumuz</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">{misyonVizyon.misyon}</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="size-12 bg-purple-600 rounded-full flex items-center justify-center">
                  <Sparkles className="size-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Vizyonumuz</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">{misyonVizyon.vizyon}</p>
            </div>
          </div>

          {/* Why Choose Us */}
          {nedenBiz.length > 0 && (
          <div className="mt-20">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Neden Ladiora?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {nedenBiz.map((item: any, index: number) => {
                const IconComp = iconMap[item.icon] || Award;
                return (
                  <div key={index} className="text-center">
                    <div className="size-20 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComp className="size-10 text-pink-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
          )}

          {/* CTA */}
          <div className="mt-20 bg-gradient-to-r from-pink-600 to-purple-600 rounded-2xl p-12 text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{cta.baslik}</h2>
            <p className="text-xl mb-8 opacity-95">{cta.altBaslik}</p>
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <a href="/" className="bg-white text-pink-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center">Alışverişe Başla</a>
              <a href="/iletisim" className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-colors inline-flex items-center justify-center">Bize Ulaşın</a>
            </div>
          </div>
        </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
